import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import Iconify from './Iconify';
import usePracticeStaff from '../hooks/usePracticeStaff';
import { SUB_LABEL, AVATAR_COLORS } from '../styles/stepFormStyles';

/**
 * Inline avatar circle selector for practitioners or assignees.
 *
 * Props:
 *   role       — 'practitioner' | 'assignee' (default: 'assignee')
 *                 'practitioner' shows blue circles (PRACTITIONER role users)
 *                 'assignee' shows purple circles (TCO/USER role users)
 *   label      — optional uppercase section label above the avatars
 *   assignToState — array of currently selected user objects
 *   setAssignTo   — setter to update selection
 *
 * Replaces both the old AssignedToLead popup AND UserSearchDropdown popup.
 */
const AssignedToLead = ({ label, assignToState = [], setAssignTo, staffRole = 'assignee' }) => {
    const { assignableUsers, bookableStaff, isLoaded } = usePracticeStaff();

    const isPractitionerMode = staffRole === 'practitioner';
    const users = isPractitionerMode ? bookableStaff : assignableUsers;
    const accentColor = isPractitionerMode ? AVATAR_COLORS.practitioner : AVATAR_COLORS.assignee;

    // String() coercion both sides: API returns `practice_user.id` as a
    // string ("1048") while usePracticeStaff returns it as a number (1048)
    // — `===` mismatches and the picker never shows an active border,
    // so a click "adds" an already-assigned user (duplicate). Coerce so
    // both sources of truth compare equal regardless of typeof.
    const handleToggle = (user) => {
        const targetId = String(user.id);
        const isSelected = assignToState?.some(a => String(a.id) === targetId);
        if (isSelected) {
            setAssignTo(assignToState.filter(a => String(a.id) !== targetId));
        } else {
            setAssignTo([...(assignToState || []), user]);
        }
    };

    if (!isLoaded) return label ? <Box><Typography sx={SUB_LABEL}>{label}</Typography><Typography sx={{ fontSize: '12px', color: '#919EAB' }}>Loading...</Typography></Box> : null;
    if (users.length === 0) return null;

    return (
        <Box>
            {label && (
                <Typography sx={SUB_LABEL}>
                    {label}
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {users.map((u) => {
                    const isSelected = assignToState?.some(a => String(a.id) === String(u.id));
                    return (
                        <Tooltip key={u.id} title={u._name} arrow placement="top"
                            componentsProps={{ tooltip: { sx: { bgcolor: accentColor, fontSize: '11px', fontWeight: 600 } }, arrow: { sx: { color: accentColor } } }}>
                            <Box onClick={() => handleToggle(u)}
                                sx={{
                                    width: 30, height: 30, borderRadius: '50%', position: 'relative',
                                    cursor: 'pointer', border: '2px solid',
                                    borderColor: isSelected ? AVATAR_COLORS.selected : 'transparent',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isSelected ? AVATAR_COLORS.selectedBg : `${accentColor}14`,
                                    fontSize: '11px', fontWeight: 600,
                                    color: isSelected ? AVATAR_COLORS.selected : accentColor,
                                    transition: 'all 150ms ease',
                                    '&:hover': { borderColor: accentColor }
                                }}>
                                {u.user?.avatar
                                    ? <img src={u.user.avatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />
                                    : u._initials}
                                {isSelected && <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: AVATAR_COLORS.selected, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff' }}><Iconify icon="eva:checkmark-fill" width={8} sx={{ color: '#fff' }} /></Box>}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

AssignedToLead.propTypes = {
    label: PropTypes.string,
    assignToState: PropTypes.array,
    setAssignTo: PropTypes.any,
    staffRole: PropTypes.oneOf(['practitioner', 'assignee']),
};

export default AssignedToLead;
